import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Button,
} from "@mui/material";
import logo from "../../img/logohq.png";
import { object, string, number, when } from "yup";
import { Formik } from "formik";
import api from "../../api";
import { IdentificationTypeEnum } from "../../enums/enums";
import { CReactSelect } from "./components/CReactSelect";
import { CTextField } from "./components/CTextField";
import moment from "moment/moment";

export const DemandRequest = ({ setIsLoading, setDataToast }) => {
  const { tipoSolicitudId } = useParams();
  const [initialValues, setInitialValues] = useState({
    tipoSolicitud: null,
    fechaSolicitud: moment(new Date()).format("DD-MM-YYYY - HH:mm:ss"),
    apellidoCliente: "",
    nombreCliente: "",
    tipoDocumento: null,
    numeroDocumento: "",
    email: null,
    codigoArea: null,
    numeroTelefono: null,
    patente: "",
    producto: null,
    motivoBaja: null,
  });

  const [requestTypes, setRequestTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [products, setProducts] = useState([]);

  const isValidDateFormat = (date) => {
    return moment(date, "DD-MM-YYYY - HH:mm:ss", true).isValid();
  };

  const validationSchema = object().shape({
    fechaSolicitud: string()
      .typeError("Requerido")
      .required("Requerido")
      .test(
        "is-valid-date",
        "Formato de fecha inválido. Use DD-MM-AAAA - hh:mm:ss",
        (value) => isValidDateFormat(value)
      ),
    tipoDocumento: object().typeError("Requerido.").required("Requerido"),
    numeroDocumento: string()
      .trim()
      .required("Requerido")
      .when("tipoDocumento", {
        is: IdentificationTypeEnum.DNI.Id,
        then: string().matches(/^[0-9]{7,8}$/, "Debe ingresar un DNI válido."),
        otherwise: string().when("tipoDocumento", {
          is: IdentificationTypeEnum.CUIT.Id,
          then: string().matches(
            /^[0-9]{10,11}$/,
            "Debe ingresar un CUIT válido."
          ),
          otherwise: string().required("Documento es requerido"), // Opcional, para manejar otros casos
        }),
      }),
    apellidoCliente: string().trim().required("Requerido"),
    nombreCliente: string().trim().required("Requerido"),
    email: string()
      .typeError("Requerido")
      .matches(
        /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g,
        "Email inválido."
      )
      .required("Requerido"),
    codigoArea: number().typeError("Solo números.").required("Requerido"),
    numeroTelefono: number().typeError("Solo números.").required("Requerido"),
    patente: string()
      .matches(
        /(^[0-9]{3}[A-Z]{3}$)|(^[A-Z]{1}[0-9]{3}[A-Z]{3}$)/g,
        "Formato inválido. Ej: 123ABC - A123BCD."
      )
      .required("Requerido"),
    producto: object().typeError("Requerido").required("Requerido"),
    motivoBaja: object().typeError("Requerido").required("Requerido"),
  });

  const toastNotification = (type, message) => {
    setDataToast({
      active: true,
      message: message,
      type: type,
    });

    document
      .getElementById("submit-button")
      .classList.remove("MuiButtonBase-root");
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      api.getRequestTypes(),
      api.getDocumentTypes(),
      api.getProductsToRequest(),
      api.getReasons(tipoSolicitudId),
    ])
      .then(
        ([
          requestTypesResponse,
          documentTypesResponse,
          productsResponse,
          reasonsResponse,
        ]) => {
          let newRequestTypes = [];
          let newDocumentTypes = [];
          let newReasons = [];
          let newProducts = [];
          document.title = "Formulario de Baja / Arrepentimiento";
          if (requestTypesResponse.data) {
            newRequestTypes = requestTypesResponse.data.map(
              ({ id, nombre }) => ({
                key: id,
                value: id,
                text: nombre.toUpperCase(),
              })
            );

            let tipoSolicitud = newRequestTypes.find(
              ({ key }) => key === +tipoSolicitudId
            );

            setInitialValues({
              ...initialValues,
              tipoSolicitud,
            });
            setRequestTypes(newRequestTypes);
          } else {
            toastNotification(
              "error",
              "Error al obtener los tipos de solicitud."
            );
          }
          if (documentTypesResponse.data) {
            newDocumentTypes = documentTypesResponse.data
              .map(({ id, nombre }) => ({
                key: id,
                value: id,
                text: nombre,
              }))
              .filter(
                ({ key }) =>
                  key === IdentificationTypeEnum.DNI.Id ||
                  key === IdentificationTypeEnum.CUIT.Id
              );
            setDocumentTypes(newDocumentTypes);
          } else {
            toastNotification(
              "error",
              "Error al obtener los tipos de documento."
            );
          }
          if (reasonsResponse.data) {
            newReasons = reasonsResponse.data.map(({ id, nombre }) => ({
              key: id,
              value: id,
              text: nombre.toUpperCase(),
            }));
            setReasons(newReasons);
          } else {
            toastNotification("error", "Error al obtener los motivos de baja.");
          }
          if (productsResponse.data) {
            newProducts = productsResponse.data.map(({ id, nombre }) => ({
              key: id,
              value: id,
              text: nombre,
            }));
            setProducts(newProducts);
          } else {
            toastNotification("error", "Error al obtener los productos.");
          }
        }
      )
      .finally(() => setIsLoading(false));
  }, []);

  const validateDocument = (value, tipoDocumento) => {
    if (tipoDocumento === IdentificationTypeEnum.DNI.Id) {
      const regex = /^[0-9]{7,8}$/g;
      if (!regex.test(value)) {
        return "Debe ingresar un DNI válido.";
      }
    } else if (tipoDocumento === IdentificationTypeEnum.CUIT.Id) {
      const regex = /^[0-9]{10,11}$/g;
      if (!regex.test(value)) {
        return "Debe ingresar un CUIT válido.";
      }
    }
    return undefined;
  };

  return (
    <Formik
      validationSchema={validationSchema}
      validate={(values) => {
        const errors = {};
        const codigoAreaLength = values.codigoArea
          ? values.codigoArea.toString().length
          : 0;
        const numeroTelefonoLength = values.numeroTelefono
          ? values.numeroTelefono.toString().length
          : 0;

        if (codigoAreaLength + numeroTelefonoLength !== 10) {
          errors.phoneLength =
            "La suma de los dos campos debe ser de 10 dígitos.";
        }

        const documentValidation = validateDocument(
          values.numeroDocumento,
          values?.tipoDocumento?.value
        );

        if (documentValidation) {
          errors.numeroDocumento = documentValidation;
        } else {
          delete errors.numeroDocumento;
        }

        return errors;
      }}
      validateOnMount
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        setIsLoading(true);
        let fechaSolicitud = moment(
          values?.fechaSolicitud,
          "DD-MM-YYYY - HH:mm:ss",
          true
        );

        api
          .postRequest({
            tipoSolicitud: +tipoSolicitudId,
            fechaSolicitud,
            apellidoCliente: String(values?.apellidoCliente).trim(),
            nombreCliente: String(values?.nombreCliente).trim(),
            tipoDocumento: values?.tipoDocumento?.key,
            numeroDocumento: +values?.numeroDocumento,
            email: String(values?.email).trim(),
            codigoArea: values?.codigoArea,
            numeroTelefono: values?.numeroTelefono,
            patente: String(values?.patente).trim(),
            productoId: values?.producto?.key,
            motivoBaja: values?.motivoBaja?.key,
          })
          .then(() =>
            toastNotification(
              "success",
              "Solicitud de baja enviada correctamente"
            )
          )
          .catch(() => toastNotification("error", "Ocurrió un error"))
          .finally(() => setIsLoading(false));
      }}
    >
      {({
        handleSubmit,
        handleChange,
        errors,
        values,
        setFieldValue,
        setTouched,
        touched,
        isValid,
      }) => (
        <div style={{ backgroundColor: "#F6F6F6", height: "100vh" }}>
          <AppBar
            position="static"
            style={{
              backgroundColor: "#000",
              height: "48px",
              minHeight: "48px",
            }}
          >
            <Toolbar style={{ height: "48px", minHeight: "48px" }}>
              <img src={logo} width="66px" alt="Logo" />
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              backgroundColor: "#F6F6F6",
              justifyContent: "center",
            }}
          >
            <Container>
              <Typography
                marginTop="15px !important"
                fontSize="36px"
                fontWeight={600}
                align="center"
                color="#CB2128"
              >
                Solicitud de baja
              </Typography>
              <Typography color="#1A1A1A" fontSize="21px" align="center">
                Completá los datos correspondientes para gestionar la baja de tu
                producto.
              </Typography>

              <Card
                sx={{
                  marginTop: 4,
                  borderRadius: "10px",
                  padding: "40px 80px",
                }}
              >
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid container spacing={5} justifyContent={"center"}>
                      <Grid item xs={12} sm={6} md={4}>
                        <CReactSelect
                          label="Tipo de solicitud *"
                          value={values?.tipoSolicitud}
                          placeholder="Seleccionar..."
                          name="tipoSolicitud"
                          options={requestTypes}
                          disabled
                          canClear={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <CTextField
                          label="Fecha de solicitud"
                          placeholder="DD-MM-AAAA - hh:mm:ss"
                          name="fechaSolicitud"
                          readOnly
                          onBlur={() =>
                            setTouched({
                              ...touched,
                              fechaSolicitud: true,
                            })
                          }
                          onChange={(e) =>
                            setFieldValue("fechaSolicitud", e.target.value)
                          }
                          value={values?.fechaSolicitud}
                          error={
                            touched?.fechaSolicitud && errors?.fechaSolicitud
                          }
                          helperText={
                            touched?.fechaSolicitud && errors?.fechaSolicitud
                          }
                          required
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider
                        sx={{ borderWidth: "3px", borderColor: "#CCCCCC" }}
                      />
                      <Typography
                        fontSize="18px"
                        fontWeight={600}
                        marginTop="24px"
                      >
                        Datos del cliente:
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <CTextField
                        label="Nombre/s"
                        name="nombreCliente"
                        onBlur={() =>
                          setTouched({ ...touched, nombreCliente: true })
                        }
                        onChange={handleChange}
                        value={values?.nombreCliente}
                        error={touched?.nombreCliente && errors?.nombreCliente}
                        helperText={
                          touched?.nombreCliente && errors?.nombreCliente
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CTextField
                        label="Apellido"
                        name="apellidoCliente"
                        onBlur={() =>
                          setTouched({ ...touched, apellidoCliente: true })
                        }
                        onChange={handleChange}
                        value={values?.apellidoCliente}
                        error={
                          touched?.apellidoCliente && errors?.apellidoCliente
                        }
                        helperText={
                          touched?.apellidoCliente && errors?.apellidoCliente
                        }
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CReactSelect
                        label="Tipo de documento *"
                        value={values?.tipoDocumento}
                        placeholder="Seleccionar..."
                        name="tipoDocumento"
                        onChange={(e) => setFieldValue("tipoDocumento", e)}
                        onBlur={() =>
                          setTouched({ ...touched, tipoDocumento: true })
                        }
                        error={touched?.tipoDocumento && errors?.tipoDocumento}
                        options={documentTypes}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CTextField
                        label="Nro. de documento"
                        name="numeroDocumento"
                        type="number"
                        onBlur={() =>
                          setTouched({ ...touched, numeroDocumento: true })
                        }
                        onChange={handleChange}
                        value={values?.numeroDocumento}
                        error={
                          touched?.numeroDocumento && errors?.numeroDocumento
                        }
                        helperText={
                          touched?.numeroDocumento && errors?.numeroDocumento
                        }
                        fullWidth
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CTextField
                        label="Email"
                        name="email"
                        onBlur={() => setTouched({ ...touched, email: true })}
                        onChange={handleChange}
                        value={values?.email}
                        error={touched?.email && errors?.email}
                        type="email"
                        helperText={touched?.email && errors?.email}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <CTextField
                        label="Cód. de área"
                        name="codigoArea"
                        type="number"
                        onBlur={() =>
                          setTouched({ ...touched, codigoArea: true })
                        }
                        onChange={handleChange}
                        value={values?.codigoArea}
                        error={touched?.codigoArea && errors?.codigoArea}
                        helperText={touched?.codigoArea && errors?.codigoArea}
                        fullWidth
                        required
                      />
                      {!(touched?.codigoArea && errors?.codigoArea) && (
                        <Typography
                          fontStyle={"italic"}
                          variant="caption"
                          color="textSecondary"
                        >
                          Ingrese sin 0.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CTextField
                        label="Nro. de teléfono"
                        name="numeroTelefono"
                        type="number"
                        onBlur={() =>
                          setTouched({ ...touched, numeroTelefono: true })
                        }
                        onChange={handleChange}
                        value={values?.numeroTelefono}
                        error={
                          (touched?.numeroTelefono && errors?.numeroTelefono) ||
                          (touched.codigoArea &&
                            touched.numeroTelefono &&
                            errors.phoneLength)
                        }
                        helperText={
                          (touched?.numeroTelefono && errors?.numeroTelefono) ||
                          (touched.codigoArea &&
                            touched.numeroTelefono &&
                            errors.phoneLength)
                        }
                        fullWidth
                        required
                      />
                      {!(
                        (touched?.numeroTelefono && errors?.numeroTelefono) ||
                        (touched.codigoArea &&
                          touched.numeroTelefono &&
                          errors.phoneLength)
                      ) && (
                        <Typography
                          fontStyle={"italic"}
                          variant="caption"
                          color="textSecondary"
                        >
                          Ingrese sin 15 y sin guiones.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <CTextField
                        label="Dominio/Patente"
                        name="patente"
                        onBlur={() => setTouched({ ...touched, patente: true })}
                        onChange={(e) => {
                          e.target.value = String(e.target.value)
                            .trim()
                            .toUpperCase();
                          handleChange(e);
                        }}
                        value={values?.patente}
                        error={touched?.patente && errors?.patente}
                        helperText={touched?.patente && errors?.patente}
                        fullWidth
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <CReactSelect
                        label="Producto *"
                        placeholder="Seleccionar..."
                        canClear={false}
                        name="producto"
                        onChange={(e) => setFieldValue("producto", e)}
                        onBlur={() =>
                          setTouched({ ...touched, producto: true })
                        }
                        value={values?.producto}
                        error={touched?.producto && errors?.producto}
                        options={products}
                      />
                      {!(touched?.producto && errors?.producto) && (
                        <Typography
                          fontStyle={"italic"}
                          variant="caption"
                          color="textSecondary"
                        >
                          Podés consultar cuál es tu producto en el mail de
                          bienvenida.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CReactSelect
                        label="Motivo de la baja *"
                        placeholder="Seleccionar..."
                        canClear={false}
                        name="motivoBaja"
                        onChange={(e) => setFieldValue("motivoBaja", e)}
                        onBlur={() =>
                          setTouched({ ...touched, motivoBaja: true })
                        }
                        value={values?.motivoBaja}
                        error={touched?.motivoBaja && errors?.motivoBaja}
                        options={reasons}
                      />
                    </Grid>
                  </Grid>

                  <Box textAlign="center" sx={{ marginTop: 4 }}>
                    <Button
                      id="submit-button"
                      onClick={handleSubmit}
                      disabled={!isValid}
                      variant="contained"
                      color="error"
                      size="large"
                      style={{ width: "180px", fontWeight: "bold" }}
                    >
                      ENVIAR
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Container>
          </Box>
        </div>
      )}
    </Formik>
  );
};
